import { Badge, Button, Dropdown, Select, Space, Typography } from "antd"
import Layout from "modules/layout/dashboard"

import styles from "./styles.module.scss"
import Loader from "components/loader"
import { useTranslation } from "react-i18next"
import { useMember } from "../../queries"
import { useToSubscribeActivities } from "./queries"
import { useMemberId } from "pages/member"
import { useSearchParams } from "react-router-dom"
import Search from "antd/es/input/Search"
import useOptions from "utils/use-options"
import { useExtCurrentSession, useExtSessions } from "../list/queries"
import ActivityItem from "./activityItem"
import { useState } from "react"
import { ReactComponent as FilterIcon } from "assets/icons/filter.svg"
import type { MenuProps } from "antd"

const { Title } = Typography
const { Main } = Layout
export const SESSION_PARAM = "session"

const Registration: React.FC = () => {
  const memberId = useMemberId()
  const member = useMember(memberId)
  const { t } = useTranslation()
  const currentSession = useExtCurrentSession()
  const sessions = useExtSessions(
    {
      filter: {
        status: { values: ["published", "progress"], operator: "in" },
      },
    },
    {
      onSuccess(data) {
        if (!searchParams.has(SESSION_PARAM)) {
          const currentSession = data.find(
            (session) => session.status === "progress"
          )
          currentSession && handleSessionFilter(currentSession.id)
        }
      },
    }
  )

  const [searchParams, setSearchParams] = useSearchParams()

  const { data, isLoading } = useToSubscribeActivities(
    searchParams.get(SESSION_PARAM) || currentSession.data?.id!
  )

  const options = useOptions(data)

  const [priceFilter, setPriceFilter] = useState<string | null>(null)
  const [subscriptionFilter, setSubscriptionFilter] = useState<boolean | null>(
    null
  )
  const [memberOnlyFilter, setMemberOnlyFilter] = useState<boolean | null>(null)

  // Get unique price options
  const uniquePrices = [
    ...new Set(data?.map((item) => item.activity_unit_price_offer)),
  ].sort((a, b) => Number(a) - Number(b))

  // Modify the options to include the new filters
  const filteredOptions = options.options?.filter((activity) => {
    if (priceFilter && activity.activity_unit_price_offer !== priceFilter) {
      return false
    }
    if (
      subscriptionFilter !== null &&
      activity.subscription !== subscriptionFilter
    ) {
      return false
    }
    if (
      memberOnlyFilter !== null &&
      activity.member_only !== memberOnlyFilter
    ) {
      return false
    }
    return true
  })

  const handleSessionFilter = (value: string) => {
    searchParams.set(SESSION_PARAM, value)
    setSearchParams(searchParams, { replace: true })
  }

  const menuItems: MenuProps["items"] = [
    {
      key: "price",
      type: "group",
      label: t("global.price"),
      children: [
        {
          key: "price-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("global.price")}
              allowClear
              value={priceFilter}
              onChange={(value) => setPriceFilter(value ?? null)}
              options={uniquePrices.map((price) => ({
                value: price,
                label: price,
              }))}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "member_only",
      type: "group",
      label: t("events.members_only"),
      children: [
        {
          key: "member-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("events.members_only")}
              allowClear
              value={memberOnlyFilter}
              onChange={(value) => setMemberOnlyFilter(value ?? null)}
              options={[
                { value: true, label: t("global.yes") },
                { value: false, label: t("global.no") },
              ]}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "subscription",
      type: "group",
      label: t("events.subscription_required"),
      children: [
        {
          key: "subscription-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("events.subscription_required")}
              allowClear
              value={subscriptionFilter}
              onChange={(value) => setSubscriptionFilter(value ?? null)}
              options={[
                { value: true, label: t("global.yes") },
                { value: false, label: t("global.no") },
              ]}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
  ]

  return (
    <>
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("events.presubscriptions"),
          },
        ]}
        title={<Title level={3}> {t("events.presubscriptions")} </Title>}
        rightContent={
          <Space>
            <Select
              value={searchParams.get(SESSION_PARAM)}
              style={{ width: "100%" }}
              placeholder={t("global.select", {
                entity: t("events.session"),
                context: "female",
              })}
              loading={sessions.isLoading}
              onChange={handleSessionFilter}
              options={sessions.data?.map((session) => ({
                value: session.id,
                label: session.name,
              }))}
            />
            <Search
              value={options.search}
              onChange={(e) => {
                options.setSearch(e.target.value)
              }}
              placeholder={t("global.search")}
              style={{ width: "100%" }}
            />
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text">
                <Badge
                  dot={[priceFilter, subscriptionFilter, memberOnlyFilter].some(
                    (f) => f !== null
                  )}
                >
                  <FilterIcon />
                </Badge>
              </Button>
            </Dropdown>
          </Space>
        }
      />
      {isLoading || currentSession.isLoading ? (
        <Loader />
      ) : (
        <div className={styles.cardsContainer}>
          {filteredOptions?.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              member={member.data}
            />
          ))}
        </div>
      )}
    </>
  )
}

export default Registration
